import { useContext, useMemo } from "react";

import { CoreWindowId } from "./constants";
import { DispatchContext, DispatchWindows, State, StateContext, WindowEntry } from "./reducer";

export interface IdentifiedWindowEntry extends WindowEntry {
    id: string;
}

export function useWindows(): State {
    const context = useContext(StateContext);

    if (context === undefined) {
        throw new Error("useWindows must be used within a WindowsProvider");
    }

    return context;
}

export function useDispatchWindows(): DispatchWindows {
    const context = useContext(DispatchContext);

    if (context === undefined) {
        throw new Error("useDispatchWindows must be used within a WindowsProvider");
    }

    return context;
}

export function useWindow(windowId: CoreWindowId) {
    const windows = useWindows();
    return windows[windowId];
}

/** Open, docked windows in render order (ascending `dockOrder`) — what `DockManager` renders. */
export function useDockedWindows(): IdentifiedWindowEntry[] {
    const windows = useWindows();
    return useMemo(
        () =>
            Object.entries(windows)
                .filter(([, entry]) => entry.state.open && entry.state.docked)
                .map(([id, entry]) => ({ id, ...entry }))
                .sort((a, b) => a.state.dockOrder - b.state.dockOrder),
        [windows],
    );
}

/** Open, non-docked windows — what `WindowManager` renders. Complement of `useDockedWindows`. */
export function useFloatingWindows(): IdentifiedWindowEntry[] {
    const windows = useWindows();
    return useMemo(
        () =>
            Object.entries(windows)
                .filter(([, entry]) => entry.state.open && !entry.state.docked)
                .map(([id, entry]) => ({ id, ...entry })),
        [windows],
    );
}

/** Open windows, docked and floating, as identified entries. */
export function useAllOpenWindows(): IdentifiedWindowEntry[] {
    const windows = useWindows();
    return useMemo(
        () =>
            Object.entries(windows)
                .filter(([, entry]) => entry.state.open)
                .map(([id, entry]) => ({ id, ...entry })),
        [windows],
    );
}
